import { useEffect } from "react";

interface SeoProperties {
  title: string;
  description: string;
  image?: string;
  type?: "website" | "article";
  structuredData?: Record<string, unknown>;
}

export function Seo({
  title,
  description,
  image,
  type = "website",
  structuredData
}: SeoProperties) {
  const structuredJson = structuredData
    ? JSON.stringify(structuredData).replaceAll("<", "\\u003c")
    : null;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    const cleanups = [
      setMeta("name", "description", description),
      setMeta("property", "og:title", title),
      setMeta("property", "og:description", description),
      setMeta("property", "og:type", type),
      setMeta("name", "twitter:card", image ? "summary_large_image" : "summary"),
      setMeta("name", "twitter:title", title),
      setMeta("name", "twitter:description", description)
    ];
    if (image) {
      cleanups.push(setMeta("property", "og:image", image));
      cleanups.push(setMeta("name", "twitter:image", image));
    }

    let structuredScript: HTMLScriptElement | null = null;
    if (structuredJson) {
      structuredScript = document.createElement("script");
      structuredScript.type = "application/ld+json";
      structuredScript.dataset.eafSeo = "structured-data";
      structuredScript.text = structuredJson;
      document.head.append(structuredScript);
    }

    return () => {
      document.title = previousTitle;
      cleanups.reverse().forEach((cleanup) => cleanup());
      structuredScript?.remove();
    };
  }, [description, image, structuredJson, title, type]);
  return null;
}

function setMeta(attribute: "name" | "property", key: string, content: string): () => void {
  const selector = `meta[${attribute}="${key}"]`;
  let meta = document.head.querySelector<HTMLMetaElement>(selector);
  const created = !meta;
  const previousContent = meta?.content;
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attribute, key);
    meta.dataset.eafSeo = "true";
    document.head.append(meta);
  }
  meta.content = content;
  return () => {
    if (created) meta.remove();
    else if (previousContent === undefined) meta.removeAttribute("content");
    else meta.content = previousContent;
  };
}
